import { Box } from '@mui/material';
import { AxisBottom, AxisRight } from '@visx/axis';
import { localPoint } from '@visx/event';
import { useParentSize } from '@visx/responsive';
import { scaleLinear, scaleTime } from '@visx/scale';
import { AreaClosed, Bar, LinePath } from '@visx/shape';
import { withTooltip } from '@visx/tooltip';
import { bisector, extent, max, min } from 'd3-array';
import { timeFormat } from 'd3-time-format';
import { useCallback, useId, useMemo } from 'react';
import { roundNum } from '../../../helpers';
import ChartTooltip from './ChartTooltip';

const DEFAULT_MARGIN = { top: 20, right: 30, bottom: 50, left: 20 };

const DEFAULT_SERIES = {
  setting: {
    point: { opacity: 0 },
    line: { stroke: "#174C86" },
    area: { fill: "#174C86", opacity: 0.4 },
    label: { fs: "26px" },
  },
  type: "area",
  scale: "linear",
  curved: true,
  seq: 1,
  legend: {
    x: 0.1,
    y: 0.04,
    label: "",
    rotate: 0,
    width: 500,
    height: 23,
  },
};

const formatMonthAbbr = timeFormat('%b');
const bisectDate = bisector(d => d.parsedDate).left;

const isRenderableValue = (d) => Number.isFinite(+d.value);

const getCurrentSeries = (data) => {
  if (!data?.series || !Array.isArray(data.series) || data.series.length === 0) {
    return DEFAULT_SERIES;
  }
  const lastSeries = data.series[data.series.length - 1];
  return lastSeries && typeof lastSeries === 'object' ? lastSeries : DEFAULT_SERIES;
};

const calculateFontSize = (width, height) => {
  const baseFontSize = Math.min(width, height) / 50;
  return Math.max(10, Math.min(baseFontSize, 16));
};

const formatXAxisTick = (date) => {
  const validDate = date instanceof Date ? date : new Date(date);
  const month = validDate.getMonth();
  const year = validDate.getFullYear();
  return month === 0 ? `${year}` : formatMonthAbbr(validDate);
};

const formatYAxisTick = (value) => {
  const absValue = Math.abs(value);
  if (absValue >= 1e12) return (value / 1e12) + 'T';
  if (absValue >= 1e9) return (value / 1e9) + 'B';
  if (absValue >= 1e6) return (value / 1e6) + 'M';
  if (absValue >= 1e3) return (value / 1e3) + 'K';
  return value;
};

const Chart = withTooltip(({
  data,
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipTop,
  tooltipLeft,
  _height = 300,
  _margin = DEFAULT_MARGIN,
}) => {
  const { parentRef, width } = useParentSize({ debounceTime: 150 });
  const gradientId = useId();

  const seriesData = useMemo(() => {
    if (!data?.series_data || !Array.isArray(data.series_data)) return [];

    return data.series_data
      .filter(d => d != null && d.value != null && !isNaN(d.value))
      .map(d => ({ ...d, parsedDate: new Date(d.date) }))
      .filter(d => !Number.isNaN(d.parsedDate.getTime()))
      .sort((a, b) => a.parsedDate.valueOf() - b.parsedDate.valueOf());
  }, [data?.series_data]);

  const margin = _margin ?? DEFAULT_MARGIN;

  const dimensions = useMemo(() => {
    const safeWidth = width || 0;
    return {
      width: safeWidth,
      height: _height,
      margin,
      innerWidth: Math.max(0, safeWidth - margin.left - margin.right),
      innerHeight: Math.max(0, _height - margin.top - margin.bottom),
    };
  }, [width, _height, margin.top, margin.right, margin.bottom, margin.left]);

  const domains = useMemo(() => {
    if (!seriesData.length) return { xExtent: [0, 0], yMin: 0, yMax: 0 };

    return {
      xExtent: extent(seriesData, d => d.parsedDate),
      yMin: min(seriesData, d => d.value) ?? 0,
      yMax: max(seriesData, d => d.value) ?? 0,
    };
  }, [seriesData]);

  const { xScale, yScale } = useMemo(() => {
    if (!seriesData.length || dimensions.width < 50) {
      return { xScale: null, yScale: null };
    }

    const xScale = scaleTime({
      range: [dimensions.margin.left, dimensions.width - dimensions.margin.right],
      domain: domains.xExtent,
    });

    const yScale = scaleLinear({
      range: [dimensions.innerHeight + dimensions.margin.top, dimensions.margin.top],
      domain: [domains.yMin, domains.yMax],
      nice: true,
    });

    return { xScale, yScale };
  }, [seriesData.length, dimensions, domains]);

  const handleTooltip = useCallback((event) => {
    if (!xScale || !yScale || !seriesData.length) return;

    const { x } = localPoint(event) || { x: 0 };
    const x0 = xScale.invert(x);
    const x0Value = x0.valueOf();

    const index = bisectDate(seriesData, x0, 1);
    const d0 = seriesData[index - 1];
    const d1 = seriesData[index];

    let d = d0;
    if (d1 && d0) {
      d = Math.abs(x0Value - d0.parsedDate.valueOf()) > Math.abs(d1.parsedDate.valueOf() - x0Value) ? d1 : d0;
    } else if (d1) {
      d = d1;
    }
    if (!d) return;

    showTooltip({
      tooltipData: d,
      tooltipLeft: xScale(d.parsedDate),
      tooltipTop: yScale(d.value),
    });
  }, [xScale, yScale, seriesData, showTooltip]);

  const hasNegativeValues = domains.yMin < 0;
  const currentSeries = useMemo(() => getCurrentSeries(data), [data]);
  const dynamicFontSize = calculateFontSize(dimensions.width, dimensions.height);
  const isReady = seriesData.length > 0 && width >= 50 && _height >= 50;

  return (
    <Box component="div" ref={parentRef} sx={{ height: _height, width: '100%', position: 'relative' }}>
      {isReady && (
        <>
          <svg
            width={Math.round(width)}
            height={_height}
            role="graphics-document"
            aria-label="Area Chart"
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={currentSeries?.setting?.area?.fill ?? "#174C86"}
                  stopOpacity={currentSeries?.setting?.area?.opacity ?? 0.4}
                />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0.2} />
              </linearGradient>
            </defs>

            {xScale && yScale && (
              <>
                <AreaClosed
                  data={seriesData}
                  x={d => roundNum(xScale(d.parsedDate), 1)}
                  y={d => roundNum(yScale(Math.max(d.value, 0)), 1)}
                  defined={isRenderableValue}
                  yScale={yScale}
                  fill={`url(#${gradientId})`}
                />

                <AxisBottom
                  top={dimensions.height - dimensions.margin.bottom}
                  scale={xScale}
                  tickFormat={formatXAxisTick}
                  stroke="#fff"
                  strokeWidth={1}
                  strokeOpacity={1}
                  hideAxisLine={false}
                  tickStroke="#333"
                  tickLength={0}
                  numTicks={width > 370 ? 7 : 5}
                  tickComponent={({ x, y, formattedValue, ...tickProps }) => (
                    <text
                      {...tickProps}
                      x={x}
                      y={y}
                      fill="#333"
                      fontSize={`${dynamicFontSize}px`}
                      textAnchor="middle"
                    >
                      {formattedValue}
                    </text>
                  )}
                />

                {hasNegativeValues && (
                  <line
                    x1={dimensions.margin.left}
                    x2={dimensions.width - dimensions.margin.right}
                    y1={yScale(0)}
                    y2={yScale(0)}
                    stroke="#333"
                    strokeWidth={1}
                    strokeDasharray="4,4"
                  />
                )}

                <AxisRight
                  left={dimensions.innerWidth + dimensions.margin.left}
                  scale={yScale}
                  tickFormat={formatYAxisTick}
                  stroke="#fff"
                  tickStroke="#333"
                  hideAxisLine={false}
                  tickLength={0}
                  numTicks={width > 370 ? 7 : 5}
                  tickComponent={({ x, y, formattedValue, ...tickProps }) => (
                    <text
                      {...tickProps}
                      x={x + 5}
                      y={y}
                      fill="#333"
                      dy=".32em"
                      textAnchor="start"
                      fontSize={`${dynamicFontSize}px`}
                    >
                      {formattedValue}
                    </text>
                  )}
                />

                <LinePath
                  data={seriesData}
                  x={d => roundNum(xScale(d.parsedDate), 1)}
                  y={d => roundNum(yScale(d.value), 1)}
                  defined={isRenderableValue}
                  stroke={currentSeries?.setting?.line?.stroke ?? "#174C86"}
                />
              </>
            )}

            <Bar
              x={dimensions.margin.left}
              y={dimensions.margin.top}
              width={dimensions.innerWidth}
              height={dimensions.innerHeight}
              fill="transparent"
              onTouchStart={handleTooltip}
              onTouchMove={handleTooltip}
              onTouchEnd={hideTooltip}
              onMouseMove={handleTooltip}
              onMouseLeave={hideTooltip}
            />

            {tooltipData && tooltipLeft != null && tooltipTop != null && (
              <g>
                <line
                  x1={tooltipLeft}
                  x2={tooltipLeft}
                  y1={dimensions.margin.top}
                  y2={dimensions.innerHeight + dimensions.margin.top}
                  stroke="#77777770"
                  strokeWidth={1}
                  pointerEvents="none"
                  strokeDasharray="5,2"
                />
                <line
                  x1={dimensions.margin.left}
                  x2={dimensions.innerWidth + dimensions.margin.left}
                  y1={tooltipTop}
                  y2={tooltipTop}
                  stroke="#7777777d"
                  strokeWidth={1}
                  pointerEvents="none"
                  strokeDasharray="5,2"
                />
                <circle
                  cx={tooltipLeft}
                  cy={tooltipTop}
                  r={4}
                  fill={currentSeries?.setting?.line?.stroke ?? "#174C86"}
                  stroke="white"
                  strokeWidth={1}
                  pointerEvents="none"
                />
              </g>
            )}
          </svg>

          <ChartTooltip
            tooltipData={tooltipData}
            tooltipTop={tooltipTop}
            tooltipLeft={tooltipLeft}
            dimensions={dimensions}
          />
        </>
      )}
    </Box>
  );
});

export default Chart;
